/**
 * CR-8613: tick or untick Direct on a Stock Planning row.
 *
 * Direct is a property of the part itself (CR-7717), the same flag the Parts
 * List's Direct column sets, so a tick here shows ticked there and the star
 * follows it on Stock Planning and the call-off screens. It is saved through
 * the Parts List's own update, which only reaches the customer's own parts.
 * On the "All call-off suppliers" grid one part can fill several rows, and
 * every one of them follows.
 */
import { useCallback, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { callOffAPI } from '../../../lib/api';
import type { StockPlanningItem } from '../../../lib/api';
import { getApiErrorMessage } from '../../../lib/api/types/common';
import { toast } from 'sonner';

/**
 * The Stock Planning grid's Direct tick.
 *
 * @example
 *   const { toggleDirect, savingDirectIds } = useDirectProductToggle(setItems);
 * @param setItems - the grid's row setter, patched with the new flag
 * @returns `toggleDirect(item, value)` and the part ids still saving
 */
export function useDirectProductToggle(
  setItems: Dispatch<SetStateAction<StockPlanningItem[]>>,
) {
  const [savingDirectIds, setSavingDirectIds] = useState<ReadonlySet<number>>(new Set());

  const toggleDirect = useCallback(async (item: StockPlanningItem, value: boolean) => {
    const partId = item.id;
    const setFlag = (flag: boolean) => setItems(prev => prev.map(row => (
      row.id === partId ? { ...row, is_direct_product: flag } : row
    )));
    setSavingDirectIds(prev => new Set(prev).add(partId));
    setFlag(value);
    try {
      await callOffAPI.updatePart(partId, { is_direct_product: value });
      toast.success(value
        ? `${item.part_number} marked as Direct`
        : `${item.part_number} is no longer Direct`);
    } catch (err: unknown) {
      setFlag(!value);
      toast.error(getApiErrorMessage(err, 'Could not update Direct'));
    } finally {
      setSavingDirectIds(prev => {
        const next = new Set(prev);
        next.delete(partId);
        return next;
      });
    }
  }, [setItems]);

  return { toggleDirect, savingDirectIds };
}
